import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo
} from 'react'

import groupBy from 'lodash/groupBy'
import mapValues from 'lodash/mapValues'
import first from 'lodash/first'
import uniqBy from 'lodash/uniqBy'

import { viewContext } from './viewContext'; // adjust the path
import { selectOwner } from '../features/api/apiSlice'
import { useSelector } from 'react-redux';
import { useFetchParticipants } from '../common/useFetchParticipants';


const filterGroups = recipients => recipients.filter(recipient => !recipient.user)
const filterUsers = recipients => recipients.filter(recipient => recipient.user)

const RecipientContext = createContext()

export function useRecipients() {
  const context = useContext(RecipientContext);
  if (!context) {
    throw new Error('useRecipients must be used within a RecipientProvider');
  }
  return context;
}

export function RecipientProvider({ children }) {
  const [allRecipients, setAllRecipients] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const setRecipients = useCallback((recipients) => {
    setAllRecipients(recipients);
    setSelectedIds(new Set()); // clear selections on update
  }, []);

  const selectRecipients = useCallback((ids) => {
    setSelectedIds(prev => {
      const updated = new Set(prev);
      ids.forEach(id => updated.add(id));
      return updated;
    });
  }, []);

  const deselectRecipients = useCallback((ids) => {
    setSelectedIds(prev => {
      const updated = new Set(prev);
      ids.forEach(id => updated.delete(id));
      return updated;
    });
  }, []);
  

  const selectedRecipients = useMemo(() => {
    return allRecipients.filter(r => selectedIds.has(r.value));
  }, [allRecipients, selectedIds]);

  const unselectedRecipients = useMemo(() => {
    return allRecipients.filter(r => !selectedIds.has(r.value));
  }, [allRecipients, selectedIds]);

  const recipientsHash = useMemo(() => mapValues(groupBy(allRecipients, 'value'), first), [ allRecipients ])

  // A recipient can belong to several groups, so they are indexed under each of their roles rather
  // than appearing once per role in allRecipients (which would collide on id).
  const usersByRole = useMemo(() => {
    const byRole = {}

    filterUsers(allRecipients).forEach(recipient => {
      const roles = recipient.roles?.length ? recipient.roles : [recipient.role]

      roles.filter(Boolean).forEach(role => { (byRole[role] ||= []).push(recipient) })
    })

    return byRole
  }, [ allRecipients ])

  const value = {
    allRecipients,
    recipientsHash,
    usersByRole,
    selectedRecipients,
    unselectedRecipients,
    setRecipients,
    selectRecipients,
    deselectRecipients,
  };

  return (
    <RecipientContext.Provider value={value}>
      {children}
    </RecipientContext.Provider>
  );
}

export function RecipientSyncer({ page = 1 }) {
  const { unique_owner_id, threadsPerPage } = useContext(viewContext);
  const { fetch } = useFetchParticipants();

  const owner = useSelector(state => selectOwner(state, unique_owner_id))

  const { setRecipients } = useRecipients();

  useEffect(() => {
    if (!owner) return;

    const allowedGroups = owner.my_allowed_groups || [];
    const allowedUsers = owner.my_allowed_users || [];
    const roles = owner.roles || {};

    const groupRecipients = allowedGroups.map((value) => ({
      value,
      id: value,
      label: roles[value]?.display_select,
    }));

    const userRecipients = allowedUsers.map((user) => ({
      id: user.unique_id,
      value: user.unique_id,
      label: user.full_name,
      user: true,
      role: user.role,
      roles: user.roles,
      role_label: user.label
    }));

    // Guard against a duplicate id reaching MiniSearch, which throws and takes the view down.
    const all = uniqBy([...groupRecipients, ...userRecipients], 'id');
    setRecipients(all);
  }, [owner, setRecipients]);

  useEffect(() => {
    const handleAttachUserEvent = (event) => fetch()

    window.addEventListener('attachUserEvent', handleAttachUserEvent)
    window.addEventListener('rescindUserEvent', handleAttachUserEvent)

    return () => {
      window.removeEventListener('attachUserEvent', handleAttachUserEvent)
      window.removeEventListener('rescindUserEvent', handleAttachUserEvent)
    }

  }, [])

  return null; // this component just syncs; it doesn’t render anything
}